import { inject, Injectable, Injector } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { PageService, PAGE_SERVICE_SCRATCH } from './page.service';
import { GlobalVariablesService } from './global-variables.service';
import { EntityService } from './entity.service';
import { VariableRegistryService } from '../dashboard/ui-editor/blockly-interface/variable-registry.service';
import { AtlasMapService } from '../dashboard/ui-editor/blockly-interface/atlas-map.service';
import {
  buildGroupedView,
  collectMapEntries,
  GroupedView,
} from '../dashboard/ui-editor/blockly-interface/helpers/atlas-grouped-view.util';
import { AtlasGlobalMeta } from '../dashboard/ui-editor/blockly-interface/helpers/atlas-map-reconcile.util';
import { Page } from '../model/page.model';
import { Entity } from '../model/entity.model';
import { renderAtlasPickList } from '../dashboard/ui-editor/blockly-interface/helpers/atlas-picklist.util';
import { resolveCreates, ResolvedCreate, RejectedCreate, nameFromPurpose } from '../dashboard/ui-editor/blockly-interface/helpers/atlas-create.util';
import { hydrateAtlasContext } from '../dashboard/ui-editor/blockly-interface/helpers/atlas-hydrate.util';
import { AtlasContext, ForgeContextRequest } from '../dashboard/ui-editor/blockly-interface/helpers/forge.types';
import { AtlasSelectService } from './atlas-select.service';

/** What one full selection run produced. Everything is reported — nothing is silently dropped. */
export interface AtlasRunResult {
  /** The payload for Forge. Absent when the run ended without picks. */
  context?: AtlasContext;
  /**
   * Atlas's RAW answer — the `{id, why}` list, before hydration (2026-08-05).
   *
   * Returned because this, not `context`, is the durable artifact. `context` is a pure function of
   * (grouped map, picks): change the hydrator and it changes, so a recorded `context` decays into a
   * snapshot of a hydrator that no longer exists. The picks do not — they are what Atlas actually said,
   * and the payload can be regenerated from them at any time WITHOUT re-running the model.
   *
   * That is what makes the benchmark separable: record picks once per case, replay them into Forge, and
   * Forge's score stops moving when Atlas's prompt changes. It is also the only place Atlas's own quality
   * is visible — `context` has already lost the `why`, which is the part that says whether a pick was
   * reasoned or lucky.
   *
   * Until now these existed only inside `pipeline-run.ts`, which intercepted them at the transport seam
   * because this result type dropped them.
   */
  picks?: { id: string; why: string }[];
  /** Variables actually created, including any renamed on collision. */
  created: ResolvedCreate[];
  /** Creates Atlas asked for that could not be made, with the reason. */
  rejected: RejectedCreate[];
  /** Atlas's own warnings, plus hydration errors/notes. Surfaced, never swallowed. */
  warnings: string[];
  /** How many create-rounds ran before picks arrived. 0 = it picked immediately. */
  rounds: number;
  /**
   * THE PICK LIST THAT PRODUCED THIS ANSWER — the exact text Atlas was given on the final round.
   *
   * Returned because a pick can only be judged against what was on offer. A handle Atlas passed over and a
   * handle that was never rendered look identical in the picks, and they are different bugs in different
   * files: one is the prompt, the other is the crawler. It is regenerated per round, so this is the last
   * one, matching `picks`.
   */
  pickList?: string;
  /** Every create Atlas asked for, round by round, including ones the resolver rejected. */
  createRequests?: { round: number; creates: { name: string; type: string; scope: string; why: string }[] }[];
}

/**
 * AtlasAgentService — the Atlas agent's entry point for AUTONOMOUS, UI-isolated map building.
 *
 * The agent does many things; one is: when it creates a variable it calls here (handing over the page),
 * and we (re)build that page's variable map WITHOUT touching the editor UI. The isolation is achieved with
 * a long-lived SCRATCH `PageService` instance created via a child injector — the editor uses the ROOT
 * PageService singleton; this uses a fresh one, so `selectedPage` + the derived lists never collide.
 *
 * Why nothing else needs refactoring: `populateEntityListsForSelectedPage()`, `getFlatVariables()`, and
 * `reconcile()` all read/write `this` / `this.selectedPage` — inside the scratch trio, `this` IS the scratch,
 * so they operate on isolated state UNCHANGED. See docs/plans/atlas-agent-map-build.md.
 *
 * Isolation boundary (decided 2026-07-17):
 *   SCRATCH (child injector)  : PageService + VariableRegistryService + AtlasMapService  (per-page state)
 *   SHARED  (from parent root): entityService + globalVariablesService                  (project/session catalogs)
 * `globalVariablesService.atlasMeta` (global $N map + page→code registry) IS written by reconcile — that's
 * project-wide state, shared by design, not the per-page UI state we isolate.
 *
 * Save policy: GLOBAL store (atlasMeta) is persisted EAGERLY on a global-variable create; the PAGE map
 * (`page.atlasVariableMap`) is a field ON the page and is saved with the page at a checkpoint (TBD).
 */
@Injectable({ providedIn: 'root' })
export class AtlasAgentService {
  private root = inject(Injector);
  private scratch?: { pageSvc: PageService; atlasMap: AtlasMapService };

  constructor(
    private pageService: PageService,               // ROOT singleton — read the live pages list (for nav tokens)
    private entityService: EntityService,
    private globalVariablesService: GlobalVariablesService,
    private atlasSelect: AtlasSelectService,
  ) {}

  /** Build the scratch trio ONCE and reuse it (a child injector giving fresh PageService/registry/AtlasMapService). */
  private getScratch() {
    if (!this.scratch) {
      const child = Injector.create({
        parent: this.root,
        providers: [
          PageService,
          VariableRegistryService,
          AtlasMapService,
          { provide: PAGE_SERVICE_SCRATCH, useValue: true },
        ],
      });
      // Getting AtlasMapService also instantiates the scratch VariableRegistryService (its dependency),
      // which subscribes to the scratch PageService.$values — so the pages we feed below reach it.
      this.scratch = { pageSvc: child.get(PageService), atlasMap: child.get(AtlasMapService) };
    }
    return this.scratch;
  }

  /**
   * Supply an already-built scratch trio instead of letting `getScratch()` construct one.
   *
   * FOR HEADLESS RUNNERS ONLY — inert in the app, which never calls this and keeps the child-injector
   * path above. It exists because `getScratch()` asks Angular DI to CONSTRUCT `PageService` from its real
   * constructor, which drags in Firestore and the rest of its graph; a Node harness has no such providers
   * and hand-builds the trio against stubbed tokens instead (scripts/atlas-harness).
   *
   * The alternative was to re-implement `buildForgeSelectionView`'s body in the harness — which would
   * prove the harness rather than the pipeline. Everything downstream of this line, including the whole of
   * `runSelection`, then runs UNMODIFIED, which is the only reason a headless run is evidence about
   * production at all.
   */
  useScratch(scratch: { pageSvc: PageService; atlasMap: AtlasMapService }): void {
    this.scratch = scratch;
  }

  /**
   * Rebuild `page`'s atlas map in isolation. Writes `page.atlasVariableMap` (on the passed object, in memory)
   * and the shared `globalVariablesService.atlasMeta`. Saves NOTHING — callers decide persistence.
   */
  async buildMapForPage(page: Page): Promise<void> {
    const s = this.getScratch();
    s.pageSvc.selectedPage = page;
    // nav tokens: the scratch skipped its own getData(), so feed it the live pages from the root PageService.
    s.pageSvc.$values.next(await firstValueFrom(this.pageService.$values));
    const value: Entity[] = await firstValueFrom(this.entityService.$values);
    await s.pageSvc.populateEntityListsForSelectedPage(value, true); // reinitialize=true → full build incl. globalEntityList
    await s.atlasMap.reconcile(); // await: the section-data lane is async
  }

  /**
   * S1 of the Atlas→Forge pipeline (docs/plans/atlas-forge-v3.md §B): build the GROUPED VIEW of `page` —
   * the pick-list Atlas selects from. Read-only with respect to persistence: saves nothing.
   *
   * RECONCILES FIRST, deliberately. A variable that exists on the page but is missing from the map is
   * INVISIBLE to Atlas, and nothing downstream can recover — Forge only ever receives what Atlas picked.
   * Stored maps do go stale: the 2026-07-27 refresh took the same 18 benchmark pages from 0 to 438 section
   * tokens. Reconcile is additive (keep/mint/tombstone, ids pinned for life — 0 tombstones and 0 id changes
   * measured across 23 clients / 135 pages), and it is negligible beside the LLM calls that follow.
   *
   * The context below is a straight port of the benchmark harness's `pull-grouped.mjs`, which has produced
   * a correct grouped view across 22 clients and 509 pages — assembled from live services here instead of
   * from raw Firestore documents. Three details it gets right that are easy to miss:
   *   • `page` is REQUIRED — what uses each variable is worked out from the page as it stands (a global's
   *     uses: this page's only), never read from the `usages` saved on the map, which go stale.
   *   • the suffix-stripping fallback in `getEntityById` — `Budget_OTI1Nj` must resolve when the registry
   *     is keyed `Budget`, or the skeleton silently loses roots.
   *   • `pageEntites` is the IMPORT GATE and is authoritative: skeleton roots are `page.entites` ONLY.
   *     Widening it was measured (99.30% vs 99.10% match for 2.6× skeleton bloat) and rejected.
   *
   * Returns the view including `stats`. Callers MUST surface the drop counters rather than ignore them —
   * `entries === parsed + orphans + droppedUnimported + droppedLane` is the no-silent-drop invariant, and a
   * variable withheld because its entity is not imported must be distinguishable from a builder bug.
   */
  async buildForgeSelectionView(page: Page): Promise<GroupedView> {
    await this.buildMapForPage(page);

    const globals = this.globalVariablesService.globalVariable || {};
    const meta = globals.atlasMeta || {};
    // Registry keyed by ID **and by NAME** — both are required, and the reason is not obvious:
    //   • the suffix fallback below strips `Budget_OTI1Nj` → `Budget`, which is a NAME, not an id. It only
    //     resolves because names are keyed.
    //   • `resolveCanonical` looks up `localVars[v].entityName` when a variable carries no `entityId`.
    // An id key always wins (`??=`), so an entity NAMED the same as another's id cannot shadow it.
    const entities: Entity[] = await firstValueFrom(this.entityService.$values);
    const reg = new Map<string, Entity>();
    for (const e of entities) if (e?.id) reg.set(e.id, e);
    for (const e of entities) if (e?.name && !reg.has(e.name)) reg.set(e.name, e);

    // ALL THREE namespaces: page ($<letter><n>) + global ($G_<n>) + navigate ($N_<n>). A reader passing
    // only the page half gets `globalVars: []` and looks correct — hence the single collect call.
    const entries = collectMapEntries(page.atlasVariableMap || {}, meta.map || {}, page, meta.navMap || {});

    return buildGroupedView(entries, {
      getEntityById: (id: string) =>
        reg.get(id) ?? reg.get(String(id).replace(/_[A-Za-z0-9]{6}$/, '')) ?? null,
      localVars: page.localVariable || {},
      globalVars: globals,
      varDescriptions: { ...(meta.varDescriptions || {}), ...(page.localVariableDescriptions || {}) },
      // NAVIGATE targets are keyed by PAGE ID, so this is built from the live pages list rather than from
      // `page` — a nav target is almost always some OTHER page. Without it every navigation choice was made
      // on the page's name alone, and names collide badly (six variants of "myfarm" on one client).
      pageDescriptions: Object.fromEntries(
        (await firstValueFrom(this.pageService.$values) || [])
          .filter((p: any) => p?.id && (p.description || '').trim())
          .map((p: any) => [p.id, p.description.trim()]),
      ),
      agent: 'forge', // AOB_reference lane IN, section lane OUT (spotlight is the mirror)
      pageEntites: page.entites || [],
    });
  }

  /**
   * Agent created a GLOBAL variable → rebuild + PERSIST the global store (atlasMeta) eagerly. Globals are
   * session-wide, so every global var-create saves. (The page map rides `page`, saved at a checkpoint — TBD.)
   */
  async onGlobalVariableCreated(page: Page): Promise<void> {
    await this.buildMapForPage(page);
    await this.globalVariablesService.create(this.globalVariablesService.globalVariable);
  }

  /**
   * Agent created a PAGE/LOCAL variable → rebuild the page map (a field on `page`, saved at a checkpoint — TBD).
   * The global namespace didn't change, so no global-store save here.
   */
  async onPageVariableCreated(page: Page): Promise<void> {
    await this.buildMapForPage(page);
  }
  /**
   * Write each created variable's lasting DESCRIPTION, local or global.
   *
   * Locals live on `page.localVariableDescriptions`; globals live on the shared
   * `globalVariablesService.atlasMeta.varDescriptions`, and `buildForgeSelectionView` merges the two when
   * it renders. The global half was never written by either create path, so a global Atlas made was
   * described nowhere at all — a page-scoped fix would have left exactly the variables that outlive the
   * page undescribed.
   */
  private describeCreated(page: Page, resolved: ResolvedCreate[]): string[] {
    const text = (r: ResolvedCreate) => (r.purpose || r.why || '').trim();
    const undescribed: string[] = [];

    for (const r of resolved) {
      const meaning = text(r);
      if (!meaning) { undescribed.push(r.name); continue; }

      if (r.scope === 'local') {
        // A field on the page, persisted whenever the page is.
        (page as any).localVariableDescriptions = page.localVariableDescriptions || {};
        (page as any).localVariableDescriptions[r.name] = meaning;
        continue;
      }

      // `atlasMeta` is a RESERVED KEY INSIDE `globalVariable`, not a property on the service: the global
      // store is a flat name→value map and the meta rides in it under that reserved name (see
      // `buildForgeSelectionView`, which reads `globals.atlasMeta`). Writing it on the service would
      // create a field nothing reads and lose every global description in silence.
      //
      // ASSIGNED BACK, NOT DEFAULTED INTO A LOCAL. `globalVariable || {}` would hand us a throwaway object
      // on a fresh store and drop the write with no error — the same silent-loss shape this method exists
      // to close.
      const store = this.globalVariablesService.globalVariable
        || (this.globalVariablesService.globalVariable = {} as any);
      // INITIALISED WITH THE FULL SHAPE, deliberately. `AtlasMapService.ensureAtlasMeta` creates
      // `{ map, seq, pages, nextPageIndex }` and only when `atlasMeta` is FALSY — so seeding a bare `{}`
      // here would satisfy that guard while leaving the global $N map and its counter missing, and
      // reconcile would then be reading fields that do not exist. Whoever gets there first must leave the
      // block valid for the other.
      if (!store.atlasMeta) store.atlasMeta = { map: {}, seq: 0, navMap: {}, navSeq: 0, pages: {}, nextPageIndex: 0 } as AtlasGlobalMeta;
      const meta = store.atlasMeta as AtlasGlobalMeta;
      meta.varDescriptions = meta.varDescriptions || {};
      meta.varDescriptions[r.name] = meaning;
    }
    return undescribed;
  }


  /**
   * Forge asked for context it did not get — create it. STEP 2 of the ask-back loop.
   *
   * A `ForgeContextRequest` IS an Atlas create instruction: `{type, scope, holdsEntity}` are exactly what
   * `resolveCreates` consumes. The one thing it lacks is a NAME, because Forge describes what it needs and
   * never names variables — `nameFromPurpose` derives one, and `resolveName` resolves collisions as it
   * does for Atlas's own creates. So this is not a translation layer, it is the same lane entered from a
   * different door.
   *
   * `holdsEntity` is accepted in BOTH spellings. Atlas emits a handle (`$E_14`) because that is all it is
   * ever allowed to emit; Forge sees resolved entity NAMES in its payload and its type declares a name.
   * Rejecting one of them would make the loop fail on a difference that is an artifact of which agent is
   * speaking, so the handle is tried first and the name second.
   *
   * Writes variables onto `page` / the global store and reconciles so ids exist. Does NOT re-invoke
   * anything — the caller decides whether to re-select, which is what keeps the round budget in one place.
   */
  /**
   * ⚠ NO LONGER CALLED (2026-08-10). Kept registered, not wired.
   *
   * This minted variables straight from a Forge CONTEXT_REQUEST — `nameFromPurpose` derived a name and the
   * variable was written to the page without Atlas being consulted at all. That put CREATION in the wrong
   * agent: Atlas selects and creates, Forge writes steps, and a pass that cannot see the pick list was
   * deciding nothing in it would do.
   *
   * It also left permanent debris when the `kind` was wrong. A column or link ask costs nothing to get
   * wrong, because nothing is made. A `variable` ask that should have been a re-selection mints a real
   * variable next to the one that already existed, and it stays in the project. Benchmark 0.1 V16 asked
   * for "the customFields AOB list" as a create while that list was already in the pick list as $L33 —
   * satisfying it would have added an empty list beside the real one and left the plan just as broken.
   *
   * `ForgePipelineService` now routes EVERY request back through `runSelection`, which can answer with
   * picks and creates together — so Atlas picks what exists and creates only what does not, in one turn.
   * Retained because it is the only worked example of turning a `ForgeContextRequest` into a
   * `ResolvedCreate`, and because deleting it would take `nameFromPurpose`'s only caller with it.
   *
   * ⚠ AND IT BYPASSES THE GLOBAL BROKER (2026-08-12). Its create loop writes
   * `globalVariablesService.globalVariable[r.name]` directly, exactly as `runSelection` used to. That is
   * harmless while nothing calls it, and would be a silent hole the moment anything did: two pages could
   * mint the same global again with no batch, no transaction, and no record. **If this is ever rewired,
   * route it through `brokerGlobals` first.** Noted here rather than fixed, because fixing a dead path
   * makes it look tested.
   */
  /**
   * Install a GLOBAL-VARIABLE BROKER. Optional, and absent in the editor.
   *
   * Globals are the only state shared across pages, and `GlobalVariablesService.create` is a full-document
   * replace — so two pages creating one concurrently lose a write outright, and two pages creating the
   * SAME NAME is one variable created twice. Neither is answerable per page: "do these two pages mean the
   * same thing?" can only be asked where both asks are visible at once.
   *
   * The seam mirrors `useScratch`, which already proves this service can be driven from outside Angular
   * (`scripts/atlas-harness/pipeline-run.ts` supplies its own trio). Nothing is installed in the editor,
   * so the editor's behaviour is byte-for-byte what it was.
   */
  useGlobalCreator(
    creator: (requests: {
      name: string; type: string; value: any; purpose?: string; holdsEntity?: string;
    }[]) => Promise<Record<string, string>>,
  ): void {
    this.globalCreator = creator;
  }

  private globalCreator?: (requests: {
    name: string; type: string; value: any; purpose?: string; holdsEntity?: string;
  }[]) => Promise<Record<string, string>>;

  /**
   * Ask the broker for names for the GLOBAL creates in this batch. Returns asked-name → granted-name.
   *
   * Empty map when no broker is installed, which is the editor, and empty map when nothing global was
   * created — so the caller has one code path rather than two.
   */
  private async brokerGlobals(resolved: ResolvedCreate[]): Promise<Map<string, string>> {
    const out = new Map<string, string>();
    if (!this.globalCreator) return out;
    const globals = (resolved || []).filter((r) => r.scope === 'global');
    if (!globals.length) return out;

    const granted = await this.globalCreator(globals.map((r) => ({
      name: r.name,
      type: r.blocklyType,
      value: r.value,
      purpose: r.purpose,
      holdsEntity: (r as any).holdsEntity,
    })));
    for (const [asked, got] of Object.entries(granted ?? {})) out.set(asked, got);
    return out;
  }

  async applyContextRequests(
    page: Page,
    requests: ForgeContextRequest[],
  ): Promise<{ created: ResolvedCreate[]; rejected: RejectedCreate[]; warnings: string[] }> {
    const created: ResolvedCreate[] = [];
    const rejected: RejectedCreate[] = [];
    const warnings: string[] = [];
    if (!requests?.length) return { created, rejected, warnings };

    const view = await this.buildForgeSelectionView(page);
    const byHandle = new Map((view.entities || []).filter((e) => e.id).map((e) => [e.id as string, e]));
    const byName = new Map((view.entities || []).filter((e) => e.name).map((e) => [e.name, e]));

    const takenLocal = new Set(Object.keys(page.localVariable || {}));
    const takenGlobal = new Set(Object.keys(this.globalVariablesService.globalVariable || {}));

    const asCreates = requests.map((r) => ({
      name: nameFromPurpose(r.purpose, r.type),
      type: r.type,
      scope: r.scope === 'global' ? ('global' as const) : ('local' as const),
      holdsEntity: r.holdsEntity,
      // Forge's own words survive as the created variable's rationale — so a variable that appears in the
      // page's list months later can still say which generation asked for it and what for.
      why: `created for Forge: ${r.purpose}${r.reason ? ` — ${r.reason}` : ''}`,
    }));

    const res = resolveCreates(asCreates, takenLocal, takenGlobal, (h) => {
      const e = byHandle.get(h) ?? byName.get(h) ?? null;
      return e ? { entityId: e.token || e.name, entityName: e.name } : null;
    });
    rejected.push(...res.rejected);
    for (const r of res.rejected) warnings.push(`Forge asked for a ${r.type} but it could not be created: ${r.reason}`);

    if (!res.resolved.length) return { created, rejected, warnings };

    let touchedGlobal = false;
    for (const r of res.resolved) {
      if (r.scope === 'local') {
        (page as any).localVariable = page.localVariable || {};
        (page as any).localVariable[r.name] = r.value;
      } else {
        this.globalVariablesService.globalVariable[r.name] = r.value;
        touchedGlobal = true;
      }
      created.push(r);
      if (r.renamedFrom) warnings.push(`created "${r.name}" — the name "${r.renamedFrom}" was taken`);
    }
    // The description is what Atlas will READ on the next round, and without it the new variable arrives
    // with a blank meaning — the weakest possible candidate in a list where meaning is the evidence.
    // PURPOSE FIRST, why as the fallback (2026-08-10): `purpose` describes the VARIABLE and is meant to
    // outlive the run; `why` describes one function's use of it and reads as a non-sequitur next to a
    // variable someone opens six months later. Before `purpose` existed there was only `why`, so the
    // fallback is what every variable created until today carries.
    for (const n of this.describeCreated(page, res.resolved)) {
      warnings.push(`created "${n}" with no description — it will appear in every future pick list with a blank meaning`);
    }

    if (touchedGlobal) await this.onGlobalVariableCreated(page);
    else await this.onPageVariableCreated(page);
    return { created, rejected, warnings };
  }

  /**
   * S1→S4 in one call: build the view, render the pick-list, ask Atlas, apply any creates, ask again,
   * hydrate the picks into the payload Forge receives.
   *
   * THE LOOP TERMINATES BY CONSTRUCTION, not by luck. Atlas answers with picks XOR creates, so a create
   * round is necessarily followed by a re-render against the updated map — and the next call has the
   * variables it asked for. MAX_CREATE_ROUNDS is a guard against a mis-shaped suggestion oscillating
   * (Atlas asks for something the resolver rejects, sees it still missing, asks again), not the normal
   * exit: the normal exit is Atlas picking.
   *
   * PERSISTENCE MATCHES THE EXISTING HOOKS. A global create saves the global store immediately (globals
   * are session-wide and shared); a page create rides the page's own save. That is the rule already in
   * `onGlobalVariableCreated` / `onPageVariableCreated`, not a new one invented here.
   */
  async runSelection(page: Page, prompt: string, opts?: { functionName?: string; signal?: AbortSignal }): Promise<AtlasRunResult> {
    /**
     * How many times a RECORD-CARRYING create may restart the selection (2026-08-10).
     *
     * It no longer counts create-rounds in general — a scalar create costs no round at all now, it is
     * folded into the same selection. This caps only the case where the selectable surface changed shape
     * and the run genuinely has to ask again. Two is a real limit, not a formality: a third fresh run
     * asking for yet another list is a model looping, not a model converging.
     */
    const MAX_FRESH_RUNS = 2;
    const createRequests: { round: number; creates: any[] }[] = [];
    const created: ResolvedCreate[] = [];
    const rejected: RejectedCreate[] = [];
    const warnings: string[] = [];

    for (let round = 0; ; round++) {
      const view = await this.buildForgeSelectionView(page);
      // Functions and workflows are NOT variables, so they never enter the atlas map and cannot arrive in
      // the view — `renderAtlasPickList` takes them as a separate argument for exactly that reason. This
      // call omitted them until 2026-08-08, which meant the "## Existing page functions" and "## Workflows"
      // sections NEVER rendered: Forge has CALL_FUNCTION and CALL_WORKFLOW, and Atlas had no way to select
      // for either. "Call the existing recalculateTotals" was unanswerable, and it failed silently — the
      // section is skipped when empty, so the pick list looked complete.
      const pickList = renderAtlasPickList(view, {
        functions: (page.functions || []).map((f: any) => ({ name: f.name, description: f.description })),
      });
      // `round: 0` ALWAYS. The round marker exists to tell Atlas "you asked for variables last turn and
      // they exist now, so pick rather than ask again" — a hint that belonged to the two-phase flow. A
      // restart here is not a continuation: it happens because a record-carrying variable changed what is
      // selectable, and the right answer is a selection made freshly against the new list, uninfluenced by
      // one made against the old. Carrying the hint forward would bias it toward the previous shape.
      const sel = await this.atlasSelect.select(
        { prompt, pickList, functionName: opts?.functionName, round: 0 },
        opts?.signal,
      );
      if (sel.warnings?.length) warnings.push(...sel.warnings);

      if (!sel.picks?.length && !sel.creates?.length) {
        // VALID, NOT AN ERROR (2026-08-27). A function that needs no data context — SHOW_TOAST, a
        // navigate-only — returns no picks and no creates, and that is the RIGHT answer. The caller
        // in worker-graph.ts checks `!!sel.context` to set `ok`; returning without `context` here
        // made ok=false → page-worker threw "Atlas produced no context" → function failed. Hydrating
        // with an empty pick list gives `{ entities: [], variables: [] }` — truthy, so the function
        // proceeds to Forge, which can generate SHOW_TOAST / NAVIGATE_TO steps without any variables.
        const { context, errors, notes } = hydrateAtlasContext(view, { picks: [] });
        warnings.push(...errors, ...notes);
        return { context, created, rejected, warnings, rounds: round, pickList };
      }

      // NOTHING TO MAKE — the common case, and it goes straight out.
      if (!sel.creates?.length) {
        const { context, errors, notes } = hydrateAtlasContext(view, { picks: sel.picks || [] });
        // An unknown id is Atlas naming something not in the view it was given. Reported rather than
        // dropped: a pick that vanishes is indistinguishable from one never made.
        warnings.push(...errors, ...notes);
        return { context, picks: sel.picks, created, rejected, warnings, rounds: round, pickList, createRequests };
      }
      // Recorded BEFORE the resolver runs, so a create that is rejected still leaves a trace of having
      // been asked for. A rejected request and a request never made are different diagnoses.
      createRequests.push({ round, creates: sel.creates as any[] });
      if (round >= MAX_FRESH_RUNS) {
        warnings.push(`The selection restarted ${round} time(s) and Atlas is still asking to create variables — stopping. Last request: ${sel.creates.map(c => c.name).join(', ')}`);
        return { created, rejected, warnings, rounds: round, pickList, createRequests };
      }

      const takenLocal = new Set(Object.keys(page.localVariable || {}));
      const takenGlobal = new Set(Object.keys(this.globalVariablesService.globalVariable || {}));
      // The lookup reads the SAME view Atlas selected from, so a handle it names either resolves here or
      // was never offered — there is no third case where Atlas invented a plausible entity.
      const byHandle = new Map((view.entities || []).filter(e => e.id).map(e => [e.id as string, e]));
      const res = resolveCreates(sel.creates, takenLocal, takenGlobal, (h) => {
        const e = byHandle.get(h);
        return e ? { entityId: e.token || e.name, entityName: e.name } : null;
      });
      rejected.push(...res.rejected);
      for (const r of res.rejected) warnings.push(`could not create "${r.name}" (${r.type}): ${r.reason}`);

      if (!res.resolved.length) {
        // Nothing could be made, so the next round would see an unchanged list and ask again. Stop here
        // rather than spend a round proving it.
        warnings.push('None of the requested variables could be created — stopping rather than re-asking against an unchanged list.');
        return { created, rejected, warnings, rounds: round, pickList, createRequests };
      }

      // GLOBAL creates route through the broker when one is installed; LOCAL creates do not, and must
      // not — they are page-local, so no cross-page collision is possible and there is nothing to batch.
      // With no broker installed this returns an empty map and every line below behaves exactly as it
      // did before, which is the property the live editor depends on.
      const brokered = await this.brokerGlobals(res.resolved);

      let touchedGlobal = false;
      for (const r of res.resolved) {
        if (r.scope === 'local') { (page as any).localVariable = page.localVariable || {}; (page as any).localVariable[r.name] = r.value; }
        else {
          // THE BROKER'S NAME WINS. It picked inside a transaction against the live store; the name Atlas
          // asked for was chosen against a snapshot that another page may already have invalidated.
          // Writing Atlas's name would leave the page referencing a global the broker never minted.
          const final = brokered.get(r.name);
          if (final && final !== r.name) { r.renamedFrom = r.renamedFrom ?? r.name; r.name = final; }
          this.globalVariablesService.globalVariable[r.name] = r.value;
          touchedGlobal = true;
        }
        created.push(r);
        if (r.renamedFrom) warnings.push(`created "${r.name}" — the name "${r.renamedFrom}" was taken`);
      }
      // Descriptions were written on the CONTEXT-REQUEST path and NOT here, so a variable Atlas created of
      // its own accord reached the map with a blank meaning — invisible in the pick list, unexplained in
      // the editor's variable list, and the weakest possible candidate the next time anything selects.
      for (const n of this.describeCreated(page, res.resolved)) {
        warnings.push(`created "${n}" with no description — it will appear in every future pick list with a blank meaning`);
      }
      // Reconcile so the new variables have minted ids before anything reads them; persist globals eagerly.
      // This is what updates the local/global maps AND the grouped map, so the rebuilt view below sees the
      // new variables with real handles rather than as names nothing can address.
      if (touchedGlobal) await this.onGlobalVariableCreated(page);
      else await this.onPageVariableCreated(page);

      /**
       * WHAT HAPPENS NEXT — fold ALL creates into the selection and go (2026-08-19).
       *
       * Before 2026-08-19 a new RECORD-CARRYING variable (Entity / EntityList) forced a fresh selection
       * because its field routes, reference routes, and columns were not in the list Atlas selected from.
       * That re-selection is correct in principle but TIMES OUT on large pick lists: stockingDetails
       * (224K chars → 254K after creates) exceeded the Command Code timeout at 358s.
       *
       * The ask-back loop (ForgePipelineService / pipeline-run.ts) is the safety net: if Forge discovers
       * it needs a field route that did not exist during selection, it sends context_request, Atlas
       * re-selects with the updated list, and Forge retries. So the re-selection here is redundant — it
       * was designed before ask-back existed, and its cost now exceeds its value on large pages.
       *
       * ALL creates — scalar and record-carrying alike — are folded into the current selection. Forge is
       * never told which handles are minutes old; that distinction is ours, not its.
       */

      // ── Fold ALL creates into this selection and go.
      const after = await this.buildForgeSelectionView(page);
      // Reconcile mints the ids, so the handle for a variable created seconds ago is found the same way
      // any other is: by name and scope in the rebuilt view. A create that resolves to no handle here did
      // not reach the map — reported, because a silently missing variable is one Forge will be asked to
      // use and cannot.
      const extra: { id: string; why: string }[] = [];
      // A RECORD or LIST variable is not a standalone variable — the grouped view lists it as a holder under
      // its entity (`heldBy`). Searching only localVars/globalVars reported every new record variable as
      // "no handle", and Forge never received it (2026-09-28, .scratch/forge-nested-list-delete).
      const holders = (after.entities || []).flatMap((e: any) => e.heldBy || []);
      for (const r of res.resolved) {
        const pool = r.scope === 'local' ? (after.localVars || []) : (after.globalVars || []);
        const found = pool.find((v: any) => v.name === r.name)
          || holders.find((h: any) => h.name === r.name && h.scope === r.scope);
        if (found?.id) extra.push({ id: found.id, why: r.why || r.purpose || `created for this function: ${r.name}` });
        else warnings.push(`created "${r.name}" but it has no handle in the rebuilt list — Forge will not receive it`);
      }
      const merged = [...(sel.picks || []), ...extra];
      const { context, errors, notes } = hydrateAtlasContext(after, { picks: merged });
      warnings.push(...errors, ...notes);
      return { context, picks: merged, created, rejected, warnings, rounds: round, pickList, createRequests };
    }
  }
}
